import { useEffect, useMemo } from 'react'
import ButtonGroupActions from '@/components/ButtonGroupActions'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CompanyIcons from '@/icons/CompanyIcons'
import ContactIcons from '@/icons/ContactIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import TextIcons from '@/icons/TextIcons'
import UnfollowIcons from '@/icons/UnfollowIcons'
import { Tag, Flame, Snowflake, Ban, CheckCircle2 } from 'lucide-react'
import { CUSTOMER_STATUS } from '@/modules/Customer/constants'
import { CustomerLeadTypes } from '@/modules/Marketing/types/utils'
import { Typogaphy } from 'vnetwork-ui'
import { getCustomerById } from '@/modules/Customer/services'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import CompanyProfile from './CompanyProfile'
import Contact from './Contact'
import CustomerAddress from './CustomerAddress'
import CustomerLogs from './CustomerLogs'
import PinnedNotes from './PinnedNotes'
import { UnfollowLeadProps } from '@/modals/CustomerModals/UnfollowLead'
import { DeleteCustomerProps } from '@/modals/CustomerModals/DeleteCustomer'
import useBasicInformationTour from './use-basic-information-tour'
import { TFunction } from 'i18next'
import { useCustomerReadOnly } from '../CustomerReadOnlyContext'

// Lead type badge with icon and color
const LeadTypeBadge: React.FC<{
    leadType: string
    t: TFunction<'translation', undefined>
}> = ({ leadType, t }) => {
    const config: Record<
        string,
        { icon: React.ReactNode; color: string; bgColor: string }
    > = {
        'warm-lead': {
            icon: <Flame size={14} />,
            color: 'text-amber-600',
            bgColor: 'bg-amber-50',
        },
        'sale-qualified-lead': {
            icon: <CheckCircle2 size={14} />,
            color: 'text-green-600',
            bgColor: 'bg-green-50',
        },
        'cool-lead': {
            icon: <Snowflake size={14} />,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
        },
        'un-qualified-lead': {
            icon: <Ban size={14} />,
            color: 'text-red-600',
            bgColor: 'bg-red-50',
        },
    }

    const { icon, color, bgColor } = config[leadType] || {
        icon: <Tag size={14} />,
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
    }

    // Giá trị ngoài 4 node hợp lệ (vd 'UNCLASSIFIED' do form tạo KH đặt mặc định)
    // không có entry trong CustomerLeadTypes -> label undefined -> badge render rỗng.
    const label =
        CustomerLeadTypes(t).find((item) => item.value === leadType)?.label ??
        t('contact-un-classified')

    return (
        <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${bgColor} ${color}`}
        >
            {icon}
            <span className="text-sm font-medium">{label}</span>
        </div>
    )
}

const BasicInformation = () => {
    const { id } = useParams()
    const { t } = useTranslation()
    const { setModal } = useModal()
    const { registerHandler, unregisterHandler } = useTourHandler()
    const { startTour } = useBasicInformationTour()
    const isReadOnly = useCustomerReadOnly()

    const { data, renderState, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMER_DETAIL, Number(id) || 0],
        queryFn: () => getCustomerById(Number(id) || 0),
        enabled: !!id,
    })

    const { result } = data || {}

    // "Bỏ theo dõi lead" gửi status -> dead-lead qua PUT /customers/:id/status,
    // mà endpoint đó chỉ nhận from-state ∈ {qualified-prospect, cool-lead}.
    const canUnfollow =
        result?.customer_status === CUSTOMER_STATUS.COOLLEAD ||
        result?.customer_status === CUSTOMER_STATUS.QUALIFIED_PROSPECTS

    // "Phân loại Lead" đi PUT /customers/:id/classify-lead — guard duy nhất bên BE là
    // status === active. Nên khách dead-lead vẫn phân loại lại được: đây là đường ra
    // duy nhất khỏi dead-lead qua giao diện.
    const canClassify =
        canUnfollow || result?.customer_status === CUSTOMER_STATUS.DEADLEAD

    // Quick Help options for BasicInformation tab
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'classify-lead',
                label: 'Phân loại Lead',
                description: 'Phân loại theo BANT',
                selector: '[data-tour="btn-classify-lead"]',
                tourTitle: 'Phân Loại Lead',
                tourDescription:
                    'Click nút "Phân loại Lead" → Chọn loại lead → Nhấn Lưu',
                tourSteps: [
                    {
                        selector: '[data-tour="btn-classify-lead"]',
                        title: 'Phân Loại Lead',
                        content:
                            'Click nút này để phân loại khách hàng:\n• Warm Lead: Tiềm năng cao\n• Cool Lead: Tiềm năng thấp\n• Unqualified: Không đủ điều kiện',
                        demo: 'classify-lead-modal',
                    },
                ],
            },
            {
                id: 'add-contact',
                label: 'Thêm liên hệ mới',
                description: 'Thêm người liên hệ cho KH',
                selector: '[data-tour="btn-add-contact"]',
                tourTitle: 'Thêm Liên Hệ Mới',
                tourDescription:
                    'Click nút "Thêm liên hệ" → Điền thông tin → Nhấn Lưu',
                tourSteps: [
                    {
                        selector: '[data-tour="btn-add-contact"]',
                        title: 'Thêm Liên Hệ Mới',
                        content:
                            'Click nút này để thêm người liên hệ:\n• Tên, email, số điện thoại\n• Vai trò: Kỹ thuật, tài chính, hành chính...\n• Có thể tạo tài khoản đăng nhập Portal',
                        demo: 'add-contact-modal',
                    },
                ],
            },
            {
                id: 'add-note',
                label: 'Thêm ghi chú',
                description: 'Ghi chú nội bộ',
                selector: '[data-tour="btn-add-note"]',
                tourTitle: 'Thêm Ghi Chú',
                tourDescription:
                    'Click nút "Thêm ghi chú" → Nhập nội dung → Nhấn Lưu',
                tourSteps: [
                    {
                        selector: '[data-tour="btn-add-note"]',
                        title: 'Thêm Ghi Chú',
                        content:
                            'Click nút này để ghi chú nội bộ:\n• Ghi chú tình trạng, yêu cầu\n• Ghim ghi chú quan trọng\n• Lịch sử ghi chú được lưu trong Log',
                        demo: 'add-note-modal',
                    },
                ],
            },
            {
                id: 'view-contacts',
                label: 'Xem danh sách liên hệ',
                description: 'Thông tin người liên hệ',
                selector: '[data-tour="contacts-section"]',
                tourTitle: 'Danh Sách Liên Hệ',
                tourDescription:
                    'Tất cả người liên hệ của khách hàng:\n• Thông tin: Tên, email, SĐT\n• Vai trò: Kỹ thuật, tài chính, hành chính...\n• Trạng thái tài khoản Portal',
            },
            {
                id: 'view-logs',
                label: 'Xem lịch sử hoạt động',
                description: 'Timeline hoạt động',
                selector: '[data-tour="customer-logs"]',
                tourTitle: 'Lịch Sử Hoạt Động',
                tourDescription:
                    'Xem timeline các hoạt động:\n• Tạo/cập nhật thông tin\n• Giao dịch, hợp đồng\n• Tương tác với nhân viên',
            },
        ],
        []
    )

    useQuickHelp(quickHelpOptions)

    // Register tour handler when component mounts
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])
    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    return (
        <div>
            {!isReadOnly && (
                <ButtonGroupActions
                    actions={[
                        ...(canClassify
                            ? [
                                  {
                                      label: t('customer:classify_lead_type'),
                                      icon: <Tag size={16} />,
                                      onClick: () =>
                                          setModal(
                                              'CustomerModals/ClassifyLeadType/index',
                                              {
                                                  customer_id: Number(id),
                                                  current_lead_type:
                                                      result?.customer_lead_type,
                                              }
                                          ),
                                      variant: 'outlined' as const,
                                      'data-tour': 'btn-classify-lead',
                                  },
                              ]
                            : []),
                        ...(canUnfollow
                            ? [
                                  {
                                      label: t('customer:unfollow_lead'),
                                      icon: <UnfollowIcons />,
                                      onClick: () =>
                                          setModal<UnfollowLeadProps>(
                                              'CustomerModals/UnfollowLead/index',
                                              {
                                                  customer_id: Number(id),
                                                  customer_name:
                                                      result?.customer_name,
                                              }
                                          ),
                                      color: 'error' as const,
                                      'data-tour': 'btn-unfollow-lead',
                                  },
                              ]
                            : []),
                        {
                            label: t('change-to-customer-company'),
                            icon: <CompanyIcons />,
                            onClick: () =>
                                setModal(
                                    'CustomerModals/ConvertCustomer/index',
                                    {
                                        customer_id: Number(id),
                                        customer_name:
                                            data?.result?.customer_name,
                                    }
                                ),
                            disabled: !!data?.result?.company?.company_id,
                            variant: 'outlined',
                            'data-tour': 'btn-convert-company',
                        },
                        {
                            label: t('customer:delete_customer'),
                            icon: <DeleteOutlineIcons />,
                            onClick: () =>
                                setModal<DeleteCustomerProps>(
                                    'CustomerModals/DeleteCustomer/index',
                                    {
                                        customer_id: Number(id),
                                        customer_name: result?.customer_name,
                                    }
                                ),
                            color: 'error',
                            disabled:
                                result?.customer_status !==
                                CUSTOMER_STATUS.DEADLEAD,
                            'data-tour': 'btn-delete-customer',
                        },
                        {
                            label: t('add-new-contact'),
                            icon: <ContactIcons />,
                            onClick: () =>
                                setModal('CustomerModals/AddNewContact/index', {
                                    customer_id: Number(id),
                                    company_id:
                                        data?.result?.company?.company_id,
                                }),
                            color: 'success',
                            'data-tour': 'btn-add-contact',
                        },
                        {
                            label: t('add-note'),
                            icon: <TextIcons />,
                            onClick: () =>
                                setModal('CustomerModals/AddNote/index', {
                                    customer_id: Number(id),
                                }),
                            variant: 'outlined',
                            'data-tour': 'btn-add-note',
                        },
                    ]}
                />
            )}
            {result?.company && (
                <div data-tour="company-profile">
                    <CompanyProfile
                        refetch={refetch}
                        company={result?.company}
                    />
                </div>
            )}
            {result?.customer_lead_type && (
                <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100 mt-2">
                    <div className="flex items-center gap-2">
                        <Typogaphy className="text-sm text-gray-500">
                            {t('customer_lead_type')}:
                        </Typogaphy>
                        <LeadTypeBadge
                            leadType={result.customer_lead_type}
                            t={t}
                        />
                    </div>
                </div>
            )}
            <div data-tour="customer-address">
                <CustomerAddress customerId={Number(id)} refetch={refetch} />
            </div>
            <div data-tour="contacts-section">
                <Contact showInfoContact />
            </div>
            <div data-tour="pinned-notes">
                <PinnedNotes />
            </div>
            <div data-tour="customer-logs">
                <CustomerLogs />
            </div>
        </div>
    )
}

export default BasicInformation
